import React, { useState, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import * as Yup from 'yup';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';

const initialValues = {
  product: {
    name: null,
    nbMonths: null,
    price: null,
  },
  switches: {
    mo3: { selected: false },
    mo6: { selected: false },
  },
};

const CheckMark = () => {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.8981 1.5L8.34715 2.05583C6.82842 3.57785 5.20805 5.30543 3.69088 6.84862L1.59434 5.12263L0.989761 4.6253L0 5.8296L0.599707 6.32691L3.25207 8.51121L3.80302 8.96465L4.30034 8.46246C5.98054 6.77866 7.80079 4.80956 9.44905 3.15773L10 2.6019L8.8981 1.5Z"
        fill="#19141A"
      />
    </svg>
  );
};

const Bullet = () => {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="10" height="10" rx="5" fill="white" stroke="#CCD6DD" />
      <circle cx="5.5" cy="5.5" r="2.5" fill="#00BFA5" />
    </svg>
  );
};

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#00BFA5',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}));

const Step = (props) => {
  let dollarUSLocale = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const { products, switches, setSwitches, getOffers } = props;
  const [{ value: employees }] = useField('employees');
  const [{ value: product }, , { setValue: setProduct }] = useField('product');

  const initSwitchState = {
    mo3: { selected: false },
    mo6: { selected: false },
  };

  const handleSwitch = (key) => {
    // if the first one is turned on, we are turning it off so reset EVERYTHING back to default values
    if (key === 'mo3' && switches.mo3.selected === true) {
      setSwitches(initSwitchState);
    } else {
      // else just update which ever one was switched
      setSwitches((prevState) => ({
        ...prevState,
        [key]: { selected: !prevState[key].selected },
      }));
    }
  };

  useEffect(() => {
    getOffers(employees);
  }, []);

  useEffect(() => {
    updateProduct();
  }, [switches.mo3.selected, switches.mo6.selected]);

  const updateProduct = () => {
    // Update the name, months, and price
    let newName = products[1].name;
    let newMonths = products[1].nbMonths;
    let newprice = products[0].price + products[1].price;
    if (switches.mo3.selected) {
      newName = products[2].name;
      newMonths += products[2].nbMonths;
      newprice += products[2].price;
    }
    if (switches.mo6.selected) {
      newName = products[3].name;
      newMonths += products[3].nbMonths;
      newprice += products[3].price;
    }
    setProduct({ name: newName, nbMonths: newMonths, price: newprice });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <span className="text-gray-500">Employee count:</span>
        <span className="px-2 py-1 text-blue-600 bg-blue-100 rounded-lg">{`${employees}`}</span>
      </div>

      <div className="flex flex-col w-10/12 p-4 border-2 rounded-lg">
        <div className="flex flex-row mb-4">
          <div className="flex flex-row items-center gap-2" style={{ width: 416 }}>
            {Bullet()}
            <div className="font-bold">Foundational Product</div>
          </div>
          <div className="font-bold">{dollarUSLocale.format(products[0].price)}</div>
        </div>
        <div className="flex flex-row gap-6 pl-6 text-sm text-secondary">
          <div className="flex flex-row items-center w-40 gap-2">
            <div>{CheckMark()}</div>
            <div>Self-service set-up</div>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div>{CheckMark()}</div>
            <div>Evidence management</div>
          </div>
        </div>

        <div className="flex flex-row gap-6 pl-6 text-sm text-secondary">
          <div className="flex flex-row items-center w-40 gap-2">
            <div>{CheckMark()}</div>
            <div>Account co-branding</div>
          </div>

          <div className="flex flex-row items-center gap-2">
            <div>{CheckMark()}</div>
            <div>Facilities set-up</div>
          </div>
        </div>

        <div className="flex flex-row gap-6 pl-6 mb-4 text-sm text-secondary">
          <div className="flex flex-row items-center w-40 gap-2">
            <div>{CheckMark()}</div>
            <div>Bot configuration</div>
          </div>

          <div className="flex flex-row items-center gap-2">
            <div>{CheckMark()}</div>
            <div>Facility dashboards</div>
          </div>
        </div>

        <div className="flex flex-row mb-4">
          <div className="flex flex-row items-center gap-2" style={{ width: 416 }}>
            {Bullet()}
            <div className="font-bold">Employee & Site Monitoring</div>
          </div>
          <div>
            <span className="font-bold">{dollarUSLocale.format(products[1].price)}</span> for this
            period
          </div>
        </div>

        <div className="flex flex-row gap-6 pl-6 text-sm text-secondary">
          <div className="flex flex-row items-center w-40 gap-2">
            <div>{CheckMark()}</div>
            <div>Initial {products[1].nbMonths} months</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-10/12">
        <div className="flex flex-row mb-4 font-bold">
          <div>Optional Extensions</div>
        </div>
        <div
          className={`flex flex-row mb-1 ${
            switches.mo3.selected === false ? 'text-secondary' : ''
          }`}
        >
          <div className="flex flex-row items-center gap-2" style={{ width: 432 }}>
            <AntSwitch
              onChange={() => handleSwitch('mo3')}
              checked={switches.mo3.selected}
              inputProps={{ 'aria-label': 'ant design' }}
              id="toggle-3months"
            />
            <div>Additional {products[2].nbMonths} months - Employee & site monitoring</div>
          </div>
          <div>
            <span className={`${switches.mo3.selected === true ? 'font-bold' : ''}`}>
              {`+${dollarUSLocale.format(products[2].price)} `}
            </span>
            <span>for this period</span>
          </div>
        </div>
        <div
          className={`flex flex-row ${switches.mo3.selected === false ? 'opacity-50' : ''} ${
            switches.mo6.selected === false ? 'text-secondary' : ''
          }`}
        >
          <div className="flex flex-row items-center gap-2" style={{ width: 432 }}>
            <AntSwitch
              onChange={() => handleSwitch('mo6')}
              disabled={switches.mo3.selected === false}
              checked={switches.mo6.selected}
              inputProps={{ 'aria-label': 'ant design' }}
              id="toggle-6months"
            />
            <div>Additional {products[3].nbMonths} months - Employee & site monitoring </div>
          </div>
          <div>
            <span className={`${switches.mo6.selected === true ? 'font-bold' : ''}`}>
              {`+${dollarUSLocale.format(products[3].price)} `}
            </span>
            <span>for this period</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-10/12">
        <div className="flex flex-row pt-6 pb-2 font-bold border-b-2 border-black">
          <div>Total</div>
        </div>
        <div
          className="flex flex-row items-center gap-2 px-3 py-2 mt-3 rounded-md w-max"
          style={{ background: '#EAE6FF' }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.00602 3.02464e-06C7.19311 0.00119413 8.35318 0.354494 9.33942 1.0152C10.3257 1.6759 11.0937 2.61431 11.5465 3.71167C11.9992 4.80904 12.1163 6.01605 11.8828 7.17997C11.6494 8.34388 11.0759 9.41238 10.235 10.2503C9.39407 11.0882 8.32351 11.6578 7.15876 11.887C5.99402 12.1163 4.78744 11.9949 3.69171 11.5382C2.59598 11.0815 1.66034 10.3101 1.0032 9.32144C0.346052 8.33282 -0.00306557 7.17149 2.02814e-05 5.9844C0.00415254 4.39477 0.638941 2.87174 1.76501 1.74972C2.89108 0.627705 4.41638 -0.00159199 6.00602 3.02464e-06ZM5.54762 4.56C5.18762 4.56 4.83362 4.56 4.47722 4.56C4.39934 4.56072 4.32188 4.57161 4.24682 4.5924C4.20209 4.60174 4.1613 4.62459 4.12997 4.65785C4.09864 4.69111 4.07827 4.73319 4.07162 4.7784C4.05934 4.82714 4.06464 4.87865 4.08659 4.92386C4.10854 4.96907 4.14573 5.0051 4.19162 5.0256C4.30693 5.07953 4.43081 5.11283 4.55762 5.124C4.89122 5.1396 5.02562 5.286 4.97522 5.6292C4.88162 6.2712 4.75562 6.9084 4.64762 7.5492C4.57442 7.9812 4.51322 8.4144 4.44242 8.8464C4.34762 9.4284 4.89002 9.9744 5.32082 10.0464C5.57601 10.0822 5.83355 10.0986 6.09122 10.0956C6.36442 10.0953 6.63468 10.0391 6.8854 9.93056C7.13612 9.82202 7.36201 9.66339 7.54922 9.4644C7.61794 9.40108 7.66954 9.32141 7.69922 9.2328C7.70639 9.19451 7.70562 9.15515 7.69695 9.11717C7.68828 9.07919 7.6719 9.04339 7.64882 9.012C7.621 8.99459 7.58959 8.9837 7.55696 8.98017C7.52433 8.97664 7.49132 8.98055 7.46042 8.9916C7.36202 9.024 7.27442 9.0912 7.17842 9.1332C7.06622 9.18555 6.94876 9.22578 6.82802 9.2532C6.77991 9.26501 6.72981 9.26622 6.68118 9.25675C6.63256 9.24729 6.58657 9.22738 6.5464 9.19839C6.50623 9.1694 6.47284 9.13203 6.44853 9.08887C6.42422 9.04571 6.40958 8.99778 6.40562 8.9484C6.38542 8.83674 6.38179 8.72272 6.39482 8.61C6.43562 8.3316 6.49322 8.0556 6.54362 7.7796C6.68042 7.0268 6.81922 6.2736 6.96002 5.52C6.99842 5.3136 7.06322 5.1108 7.02002 4.8936C7.0167 4.84584 7.00389 4.79922 6.98233 4.75648C6.96077 4.71373 6.93091 4.67571 6.89448 4.64465C6.85805 4.61358 6.81579 4.5901 6.77018 4.57556C6.72456 4.56103 6.6765 4.55574 6.62882 4.56C6.26882 4.56 5.90762 4.56 5.54762 4.56ZM6.20042 1.8912C5.98411 1.88172 5.76987 1.93689 5.58505 2.04969C5.40023 2.16248 5.25321 2.32779 5.16274 2.5245C5.07228 2.72122 5.04247 2.94043 5.07713 3.15416C5.11178 3.36788 5.20933 3.56644 5.35732 3.72449C5.50531 3.88254 5.69704 3.99292 5.90803 4.04153C6.11902 4.09015 6.33971 4.0748 6.54194 3.99745C6.74418 3.9201 6.91878 3.78425 7.04347 3.60723C7.16816 3.43022 7.23728 3.22007 7.24202 3.0036C7.25012 2.71855 7.14515 2.44186 6.95001 2.23391C6.75487 2.02596 6.48541 1.90363 6.20042 1.8936V1.8912Z"
              fill="#5243AA"
            />
          </svg>
          <span className="text-sm">
            Payment is due within 10 days of invoice date, including applicable taxes
          </span>
        </div>

        <div className="flex flex-col gap-3 pt-6 pl-6">
          <div className="flex flex-row">
            <div style={{ width: 408 }}>Program Duration:</div>
            <div className="font-bold">
              {3 +
                (switches.mo3.selected ? products[2].nbMonths : 0) +
                (switches.mo6.selected ? products[3].nbMonths : 0)}{' '}
              months
            </div>
          </div>
          <div className="flex flex-row">
            <div style={{ width: 408 }}>Program Rate: </div>
            <div className="font-bold">
              {dollarUSLocale.format(
                products[0].price +
                  products[1].price +
                  (switches.mo3.selected ? products[2].price : 0) +
                  (switches.mo6.selected ? products[3].price : 0)
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-10/12 gap-4 text-sm text-secondary">
        <div>
          The subscription renews automatically each{' '}
          {3 +
            (switches.mo3.selected ? products[2].nbMonths : 0) +
            (switches.mo6.selected ? products[3].nbMonths : 0)}{' '}
          months unless canceled at least 30 days before the end of the term. (
          <a
            href="/legal"
            target="_blank"
            className="text-blue-400 hover:underline focus:underline focus:outline-none focus:text-blue-900 hover:text-blue-900"
          >
            Health Security Product Agreement
          </a>
          )
        </div>
        <div>
          The price is calculated without applicable state or local taxes. Such taxes may apply
          depending on location and country and will be added to the net price.{' '}
        </div>
      </div>
    </div>
  );
};

export { initialValues };
export default Step;
